import { supabase, isSupabaseConfigured } from "./supabase";

export const REPORT_MAX_ROWS = 1000;

export const AGING_BUCKET_META = {
  current: { label: "Current", tone: "neutral" },
  "1_30": { label: "1–30 days", tone: "warn" },
  "31_60": { label: "31–60 days", tone: "warn" },
  "61_90": { label: "61–90 days", tone: "alert" },
  "91_plus": { label: "91+ days", tone: "alert" },
  settled: { label: "Settled", tone: "ok" },
};

export const RECEIPT_KIND_META = {
  family_provider: { label: "Family payment", tone: "primary" },
  family_manual: { label: "Family receipt", tone: "primary" },
  agency_remittance: { label: "Agency remittance", tone: "secondary" },
};

export function emptyOperationalFinancialReport({
  asOf = new Date().toISOString().slice(0, 10),
  periodStart = asOf,
  periodEnd = asOf,
} = {}) {
  return {
    as_of: asOf,
    period: { start: periodStart, end: periodEnd },
    summary: {
      issued_cents: 0,
      cash_received_cents: 0,
      family_received_cents: 0,
      agency_received_cents: 0,
      open_balance_cents: 0,
      family_open_cents: 0,
      agency_open_cents: 0,
      open_invoice_count: 0,
      agency_claim_adjustments_cents: 0,
      agency_claim_variance_cents: 0,
    },
    aging: {
      current_cents: 0,
      age_1_30_cents: 0,
      age_31_60_cents: 0,
      age_61_90_cents: 0,
      age_91_plus_cents: 0,
    },
    row_count: 0,
    truncated: false,
    rows: [],
    receipt_count: 0,
    receipts_truncated: false,
    receipts: [],
    adjustment_count: 0,
    adjustments_truncated: false,
    adjustments: [],
  };
}

export async function getOperationalFinancialReport({
  asOf,
  periodStart,
  periodEnd,
  query = null,
  limit = 250,
} = {}) {
  const effectiveAsOf = asOf || new Date().toISOString().slice(0, 10);
  const effectivePeriodEnd = periodEnd || effectiveAsOf;
  const effectivePeriodStart = periodStart || effectivePeriodEnd;

  if (!isSupabaseConfigured) {
    return emptyOperationalFinancialReport({
      asOf: effectiveAsOf,
      periodStart: effectivePeriodStart,
      periodEnd: effectivePeriodEnd,
    });
  }

  const { data, error } = await supabase.rpc("rpc_operational_financial_report", {
    p_as_of: effectiveAsOf,
    p_period_start: effectivePeriodStart,
    p_period_end: effectivePeriodEnd,
    p_query: query?.trim() || null,
    p_limit: Math.max(1, Math.min(REPORT_MAX_ROWS, Number(limit) || 250)),
  });
  if (error) throw error;
  return data || emptyOperationalFinancialReport({
    asOf: effectiveAsOf,
    periodStart: effectivePeriodStart,
    periodEnd: effectivePeriodEnd,
  });
}

function csvCell(value) {
  const text = value == null ? "" : String(value);
  // Family names, invoice titles, and remittance references are human-entered.
  // Guard spreadsheet applications from interpreting a cell as a formula when
  // an administrator opens an operational export.
  const safeText = /^[\t\r ]*[=+\-@]/.test(text) ? `'${text}` : text;
  return /[",\n]/.test(safeText)
    ? `"${safeText.replace(/"/g, '""')}"`
    : safeText;
}

function csvText(header, rows) {
  return [
    header.map(csvCell).join(","),
    ...rows.map((row) => row.map(csvCell).join(",")),
  ].join("\n");
}

export function receivablesCsv(report) {
  const rows = report?.rows || [];
  return csvText(
    [
      "invoice_id",
      "family",
      "title",
      "issued_on",
      "due_on",
      "status",
      "invoice_total_cents",
      "cash_received_cents",
      "open_receivable_cents",
      "family_open_cents",
      "agency_open_cents",
      "agency_claim_adjustments_cents",
      "agency_claim_variance_cents",
      "age_bucket",
      "age_days",
      "currency",
    ],
    rows.map((row) => [
      row.invoice_id,
      row.family_name,
      row.title,
      row.issue_date,
      row.due_date,
      row.invoice_status,
      row.invoice_total_cents,
      row.received_cents,
      row.open_cents,
      row.family_open_cents,
      row.agency_open_cents,
      row.agency_claim_adjustments_cents,
      row.agency_claim_variance_cents,
      row.age_bucket,
      row.age_days,
      row.currency,
    ]),
  );
}

export function receiptsCsv(report) {
  const rows = report?.receipts || [];
  return csvText(
    [
      "receipt_id",
      "received_on",
      "kind",
      "family",
      "agency",
      "invoice_id",
      "amount_cents",
      "currency",
      "reference",
    ],
    rows.map((row) => [
      row.receipt_id,
      row.received_on,
      row.receipt_kind,
      row.family_name,
      row.agency_name,
      row.invoice_id,
      row.amount_cents,
      row.currency,
      row.reference,
    ]),
  );
}

export function adjustmentsCsv(report) {
  const rows = report?.adjustments || [];
  return csvText(
    [
      "adjustment_id",
      "recorded_on",
      "kind",
      "family",
      "invoice_id",
      "amount_cents",
      "currency",
    ],
    rows.map((row) => [
      row.adjustment_id,
      row.recorded_on,
      row.adjustment_kind,
      row.family_name,
      row.invoice_id,
      row.amount_cents,
      row.currency,
    ]),
  );
}

export function financialAttentionRows(report, limit = 8) {
  const rows = report?.rows || [];
  return rows
    .filter((row) => (
      Number(row.open_cents || 0) > 0
      && (
        row.age_bucket === "31_60"
        || row.age_bucket === "61_90"
        || row.age_bucket === "91_plus"
        || Number(row.agency_claim_variance_cents || 0) > 0
      )
    ))
    .slice(0, limit);
}
